/**
 * Source Explorer Domain Types
 *
 * Architecture: Clean Architecture - Domain Layer
 * These types are framework-agnostic and represent core business concepts.
 *
 * Assumptions:
 * - ColumnId is stable across imports (schema.table.column)
 * - TableId format: schema.table_name
 * - DB comments are read-only from source, analyst notes are editable
 * - Tags and Statuses are stored in dictionaries (user-editable)
 */

// ============================================================================
// Identifiers (Value Objects)
// ============================================================================

export type TSourceId = string & { readonly __brand: 'SourceId' };
export type TProjectId = string & { readonly __brand: 'ProjectId' };
export type TSchemaName = string & { readonly __brand: 'SchemaName' };
export type TTableId = string & { readonly __brand: 'TableId' };
export type TColumnId = string & { readonly __brand: 'ColumnId' };
export type TTagId = string & { readonly __brand: 'TagId' };
export type TStatusId = string & { readonly __brand: 'StatusId' };
export type TCategoryId = string & { readonly __brand: 'CategoryId' };
export type TGroupId = string & { readonly __brand: 'GroupId' };
export type TProfilingQueueId = string & { readonly __brand: 'ProfilingQueueId' };
export type TProfilingHistoryId = string & { readonly __brand: 'ProfilingHistoryId' };
export type TMetadataTableId = string & { readonly __brand: 'MetadataTableId' };
export type TMetadataColumnId = string & { readonly __brand: 'MetadataColumnId' };

// Brand helpers
//
// Идентификаторы, приходящие от продукта числом (dto-conventions-R04),
// принимаются в любой записи: клиент не выясняет, где какая форма ожидается,
// и приводит их к одному внутреннему виду. Составные идентификаторы
// («схема.таблица», «таблица.колонка») собираются здесь же и остаются
// строками — это объявленная нормой вторая форма адресации.
export function createSourceId(value: string | number): TSourceId {
  return String(value) as TSourceId;
}

export function createProjectId(value: string | number): TProjectId {
  return String(value) as TProjectId;
}

export function createSchemaName(value: string): TSchemaName {
  return value as TSchemaName;
}

export function createTableId(schema: string, table: string): TTableId {
  return `${schema}.${table}` as TTableId;
}

export function createColumnId(tableId: TTableId, column: string): TColumnId {
  return `${tableId}.${column}` as TColumnId;
}

export function createTagId(value: string | number): TTagId {
  return String(value) as TTagId;
}

export function createStatusId(value: string | number): TStatusId {
  return String(value) as TStatusId;
}

export function createCategoryId(value: string | number): TCategoryId {
  return String(value) as TCategoryId;
}

export function createProfilingQueueId(value: string | number): TProfilingQueueId {
  return String(value) as TProfilingQueueId;
}

export function createProfilingHistoryId(value: string | number): TProfilingHistoryId {
  return String(value) as TProfilingHistoryId;
}

// Идентификаторы строк кэша метаданных — числовые. Одноимённые `tableId` и
// `columnId` в срезе дерева источников это **другие** поля: там составная
// «схема.таблица[.колонка]», объявленная нормой вторая форма адресации.
export function createMetadataTableId(value: string | number): TMetadataTableId {
  return String(value) as TMetadataTableId;
}

export function createMetadataColumnId(
  value: string | number,
): TMetadataColumnId {
  return String(value) as TMetadataColumnId;
}

// ============================================================================
// Tags & Statuses (User-defined Dictionaries)
// ============================================================================

/**
 * Tag category for grouping
 */
export interface ITagCategory {
  readonly id: TCategoryId;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly displayOrder: number;
}

/**
 * Tag from dictionary (system or user-defined)
 */
export interface ITag {
  readonly id: TTagId;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly color: string | null;
  readonly icon: string | null;
  readonly categoryId: TCategoryId | null;
  readonly isSystem: boolean;
  readonly displayOrder: number;
}

/**
 * Column status from dictionary
 */
export interface IColumnStatus {
  readonly id: TStatusId;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly color: string | null;
  readonly isTerminal: boolean;
  readonly isSystem: boolean;
  readonly displayOrder: number;
}

// ============================================================================
// Enums for UI Components (mapped to system codes)
// ============================================================================

/**
 * Predefined tag types used in UI components
 * Values are the system tag codes stored in the DB dictionary.
 */
export const enum EPredefinedTag {
  PII = 'pii',
  SENSITIVE = 'sensitive',
  PUBLIC = 'public',
  BUSINESS_KEY = 'business_key',
  DIMENSION = 'dimension',
  MEASURE = 'measure',
  WATERMARK = 'watermark',
  WATERMARK_CANDIDATE = 'watermark_candidate',
  AUDIT = 'audit',
  DEPRECATED = 'deprecated',
  NEEDS_REVIEW = 'needs_review',
  QUESTION = 'question',
  CHANGED = 'changed',
}

/**
 * Column status values used in UI components
 * Values are the system status codes stored in the DB dictionary.
 */
export const enum EColumnStatus {
  NEW = 'new',
  DRAFT = 'draft',
  IN_PROGRESS = 'in_progress',
  REVIEWED = 'reviewed',
  REVIEW = 'review',
  CONFIRMED = 'confirmed',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

// ============================================================================
// Database Metadata (from introspection, read-only)
// ============================================================================

export interface IColumnConstraint {
  readonly type: 'pk' | 'fk' | 'unique' | 'check';
  readonly name: string;
  readonly referencedTable?: TTableId;
  readonly referencedColumn?: string;
}

export interface IColumnMeta {
  readonly id: TColumnId;
  readonly name: string;
  readonly dataType: string;
  readonly isNullable: boolean;
  readonly defaultValue: string | null;
  readonly ordinalPosition: number;
  readonly dbComment: string | null;
  readonly constraints: readonly IColumnConstraint[];
  readonly isPrimaryKey: boolean;
  readonly isForeignKey: boolean;
  readonly isUnique: boolean;
}

export interface ITableMeta {
  readonly id: TTableId;
  readonly schema: TSchemaName;
  readonly name: string;
  readonly dbComment: string | null;
  readonly columns: readonly IColumnMeta[];
  readonly rowCountEstimate: number | null;
  readonly sizeBytes: number | null;
  readonly hasPrimaryKey: boolean;
  readonly lastImportedAt: string | null;
  readonly tableType: TTableType;
}

export interface ISchemaInfo {
  readonly name: TSchemaName;
  readonly tableCount: number;
}

export type TTableType = 'table' | 'view' | 'materialized_view';

// ============================================================================
// Connection Types (new connection model)
// ============================================================================

export type TConnectionType = 'database' | 'ftp' | 'sftp' | 's3' | 'local_path' | 'web_api' | 'kafka';
export type TDataProviderType = 'database' | 'excel' | 'csv' | 'json' | 'xml';

export interface IFilePattern {
  readonly id: string;
  readonly sourceId: string;
  readonly name: string;
  readonly fileMask: string;
  readonly filePath: string;
  readonly dataProviderType: TDataProviderType;
  readonly providerConfig: Record<string, string>;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface IEndpoint {
  readonly id: string;
  readonly sourceId: string;
  readonly name: string;
  readonly method: string;
  readonly path: string;
  readonly dataProviderType: TDataProviderType;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ITopic {
  readonly id: string;
  readonly sourceId: string;
  readonly name: string;
  readonly topicName: string;
  readonly dataProviderType: TDataProviderType;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ITableSummary {
  readonly id: TTableId;
  readonly schema: TSchemaName;
  readonly name: string;
  readonly columnCount: number;
  readonly hasPrimaryKey: boolean;
  readonly sizeBytes: number | null;
  readonly rowCountEstimate: number | null;
  readonly tableType: TTableType;
}

export interface ISchemaHiddenTable {
  readonly schemaId: number;
  readonly tableName: string;
}

export interface ISourceHiddenSchema {
  readonly sourceId: TSourceId;
  readonly schemaId: TSchemaName;
}

export interface ISchemaTableNotePreview {
  readonly tableName: string;
  readonly note: string;
  readonly tagIds: readonly TTagId[];
}

export interface ISchemaNotePreview {
  readonly schemaId: number;
  readonly note: string;
  readonly tagIds: readonly TTagId[];
}

// ============================================================================
// Profiling Queue & History
// ============================================================================

export type TProfilingQueueStatus = 'pending' | 'processing';
export type TProfilingHistoryStatus = 'completed' | 'failed';

export interface IProfilingQueueItem {
  readonly id: TProfilingQueueId;
  readonly sourceId: TSourceId;
  readonly tableId: TMetadataTableId;
  readonly columnId: TMetadataColumnId;
  readonly schemaName: string;
  readonly tableName: string;
  readonly columnName: string;
  readonly status: TProfilingQueueStatus;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly retryCount: number;
  readonly maxRetries: number;
}

export interface IProfilingHistoryItem {
  readonly id: TProfilingHistoryId;
  readonly sourceId: TSourceId;
  readonly tableId: TMetadataTableId | null;
  readonly columnId: TMetadataColumnId | null;
  readonly schemaName: string;
  readonly tableName: string;
  readonly columnName: string;
  readonly status: TProfilingHistoryStatus;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly durationMs: number | null;
  readonly errorMessage: string | null;
  /**
   * Машиночитаемый признак причины отказа (api-errors-R06, R07).
   * `null`, когда отказа нет. Норма: features/source-explorer/profiling.md R16.
   */
  readonly errorCode: string | null;
  /** Параметры подстановки для текста причины (api-errors-R06). */
  readonly errorParams: Readonly<Record<string, string | number>> | null;
  readonly retryCount: number;
  readonly maxRetries: number;
  readonly createdAt: string;
}

export interface IProfilingSettings {
  readonly sourceId: TSourceId;
  readonly maxConcurrent: number;
  readonly windowStart: string;
  readonly windowEnd: string;
  readonly updatedAt: string;
}

export interface IProfilingSettingsUpdate {
  readonly maxConcurrent: number;
  readonly windowStart: string;
  readonly windowEnd: string;
}

// ============================================================================
// Sync Queue & History (metadata synchronization)
// ============================================================================

export type TSyncQueueStatus = 'pending' | 'processing';
export type TSyncHistoryStatus = 'completed' | 'failed';

export interface ISyncQueueItem {
  readonly id: number;
  readonly sourceId: TSourceId;
  readonly status: TSyncQueueStatus;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly errorMessage: string | null;
  /**
   * Машиночитаемый признак причины отказа (api-errors-R06, R07).
   * `null`, когда отказа нет. Норма: features/source-explorer/tree-navigation.md R33.
   */
  readonly errorCode: string | null;
  /** Параметры подстановки для текста причины (api-errors-R06). */
  readonly errorParams: Readonly<Record<string, string | number>> | null;
  /**
   * Задача возвращена в ожидание перезапуском продукта
   * (data-processing-metadata-sync-R10).
   *
   * Отказом это не является и в полях отказа не выражается (api-errors-R12):
   * попытка не потрачена, задача выполнится сама. У состояния свой текст, и
   * показывать его как ошибку нельзя.
   */
  readonly returnedAfterRestart: boolean;
  readonly retryCount: number;
  readonly maxRetries: number;
}

export interface ISyncHistoryItem {
  readonly id: number;
  readonly sourceId: TSourceId;
  readonly status: TSyncHistoryStatus;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly durationMs: number | null;
  readonly schemasCount: number | null;
  readonly tablesCount: number | null;
  readonly columnsCount: number | null;
  readonly errorMessage: string | null;
  /**
   * Машиночитаемый признак причины отказа (api-errors-R06, R07).
   * `null`, когда отказа нет. Норма: features/source-explorer/tree-navigation.md R33.
   */
  readonly errorCode: string | null;
  /** Параметры подстановки для текста причины (api-errors-R06). */
  readonly errorParams: Readonly<Record<string, string | number>> | null;
  readonly retryCount: number;
  readonly maxRetries: number;
  readonly createdAt: string;
}

// ============================================================================
// Column Profiling (statistics)
// ============================================================================

export interface ITopValue {
  readonly value: string;
  readonly count: number;
  readonly percentage: number;
}

/**
 * Числовые показатели профиля.
 *
 * Норма: spec/features/source-explorer/profiling.md, R12 — недоступность
 * отдельного показателя не скрывает остальные показатели группы (у таблицы из
 * одной строки нет стандартного отклонения, но есть минимум, максимум, среднее
 * и медиана). Поэтому каждое поле самостоятельно.
 */
export interface INumericStats {
  readonly min?: number | null;
  readonly max?: number | null;
  readonly avg?: number | null;
  readonly median?: number | null;
  readonly stddev?: number | null;
}

/** Текстовые показатели профиля; поля самостоятельны по той же причине (R12). */
export interface ITextStats {
  readonly minLength?: number | null;
  readonly maxLength?: number | null;
  readonly avgLength?: number | null;
}

export interface IColumnProfile {
  readonly columnName: string;
  readonly dataType: string;
  readonly totalRows: number;
  readonly nonNullCount: number;
  readonly nullPercentage: number;
  readonly distinctCount: number | null;
  readonly numericStats: INumericStats | null;
  readonly textStats: ITextStats | null;
  readonly topValues: readonly ITopValue[];
  /** Момент последнего профилирования колонки (R11: сохраняется и показывается). */
  readonly profiledAt?: string | null;
}

// ============================================================================
// Analyst Notes (editable, stored in database)
// ============================================================================

/**
 * Column tags structure used in UI components
 * Contains both predefined system tags and custom user tags
 */
export interface IColumnTags {
  readonly predefined: readonly EPredefinedTag[];
  readonly custom: readonly string[];
}

export interface IColumnNote {
  readonly columnId: TColumnId;
  readonly note: string;
  readonly tagIds: readonly TTagId[];
  readonly tags: IColumnTags;
  readonly status: EColumnStatus | null;
  readonly statusId: TStatusId | null;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface ITableNote {
  readonly tableId: TTableId;
  readonly note: string;
  readonly tagIds: readonly TTagId[];
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface ITableNotes {
  readonly tableNote: ITableNote | null;
  readonly columnNotes: ReadonlyMap<TColumnId, IColumnNote>;
}

// ============================================================================
// Coverage Statistics
// ============================================================================

export interface ICoverageStats {
  readonly totalColumns: number;
  readonly columnsWithNotes: number;
  readonly needsReviewCount: number;
  readonly piiCount: number;
  readonly noDbCommentCount: number;
  readonly changedSinceLastImportCount: number;
  readonly reviewedCount: number;
  readonly confirmedCount: number;
  /** Count per tag code */
  readonly tagCounts: Readonly<Record<string, number>>;
}

// ============================================================================
// Filter Types
// ============================================================================

export const enum EColumnFilter {
  ONLY_WITH_NOTES = 'only_with_notes',
  ONLY_WITHOUT_NOTES = 'only_without_notes',
  PII = 'pii',
  KEYS = 'keys',
  WATERMARK_CANDIDATES = 'watermark_candidates',
  NEEDS_REVIEW = 'needs_review',
  CHANGED = 'changed',
}

export const enum ESortField {
  NAME = 'name',
  TYPE = 'type',
  ORDINAL = 'ordinal',
  STATUS = 'status',
}

export const enum ESortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export interface IColumnSort {
  readonly field: ESortField;
  readonly direction: ESortDirection;
}

export interface IColumnFilters {
  readonly activeFilters: readonly EColumnFilter[];
  readonly searchQuery: string;
  readonly sort: IColumnSort;
  readonly reviewModeEnabled: boolean;
}

// ============================================================================
// Batch Operations
// ============================================================================

export interface IBatchTagUpdate {
  readonly columnIds: readonly TColumnId[];
  readonly addTagIds: readonly TTagId[];
  readonly removeTagIds: readonly TTagId[];
}

export interface IBatchStatusUpdate {
  readonly columnIds: readonly TColumnId[];
  readonly statusId: TStatusId;
}

export interface IBatchNoteAppend {
  readonly columnIds: readonly TColumnId[];
  readonly noteToAppend: string;
}

// ============================================================================
// UI State Types (not domain, but needed for MVI)
// ============================================================================

export type TWorkspaceTab = 'columns' | 'schema' | 'data';

export interface ISampleDataQuery {
  readonly whereSql: string;
  readonly orderBySql: string;
}

export interface ISelectionState {
  readonly selectedColumnIds: ReadonlySet<TColumnId>;
  readonly focusedColumnId: TColumnId | null;
  readonly isInspectorOpen: boolean;
}

export interface IInlineEditState {
  readonly editingColumnId: TColumnId | null;
  readonly editingValue: string;
  readonly isDirty: boolean;
}

// ============================================================================
// API Response Types (for validation)
// ============================================================================

export interface IApiError {
  readonly code: string;
  readonly message: string;
  readonly details?: Record<string, unknown>;
  /** Parameters for i18n interpolation (e.g., {count: 2}) */
  readonly params?: Record<string, unknown>;
}

export interface IApiResponse<T> {
  readonly success: boolean;
  readonly data?: T;
  readonly error?: IApiError;
}

// ============================================================================
// Create Tag Request
// ============================================================================

export interface ICreateTagRequest {
  readonly code: string;
  readonly name: string;
  readonly description?: string;
  readonly color?: string;
  readonly categoryId?: TCategoryId;
}

// ============================================================================
// Sample Data (for quick preview)
// ============================================================================

/**
 * Sample data для таблицы (100 случайных строк)
 */
export interface ISampleData {
  readonly columns: readonly string[];
  readonly rows: readonly (readonly unknown[])[];
  readonly totalRows: number;
  readonly cachedAt: string;
}
